import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, comics } from '@/mocks/comics';

const sortOptions = [
  { id: 'hot', label: '最热' },
  { id: 'new', label: '最新' },
  { id: 'liked', label: '最多点赞' },
];

export default function ComicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') || 'all';
  const [sort, setSort] = useState('hot');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    document.title = '漫画合集 · 短篇日常搞笑减压快看 | 韩国歪歪漫画';
  }, []);

  const filtered = useMemo(() => {
    let list = cat === 'all' ? [...comics] : comics.filter((c) => c.category === cat);
    if (keyword.trim()) {
      const kw = keyword.trim();
      list = list.filter(
        (c) =>
          c.title.includes(kw) ||
          c.tags.some((t) => t.includes(kw)) ||
          c.categoryName.includes(kw),
      );
    }
    if (sort === 'new') list = list.filter((c) => c.isNew || c.updatedAt.includes('今日')).concat(list.filter((c) => !c.isNew && !c.updatedAt.includes('今日')));
    if (sort === 'liked') list = [...list].sort((a, b) => parseFloat(b.likes) - parseFloat(a.likes));
    return list;
  }, [cat, sort, keyword]);

  const activeCat = categories.find((c) => c.id === cat);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="bg-background-100 pt-24 pb-8 md:pt-32 md:pb-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="text-xs text-foreground-600" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-900">漫画合集</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-4xl text-foreground-950 md:text-5xl">
            {activeCat ? activeCat.name : '全部短篇漫画'}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            短篇日常搞笑合集，摸鱼、睡前、通勤碎片时间快看。减压内容持续更新，热门短篇分类齐全，点开就能找到你的轻松入口。
          </p>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-sm">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题或标签，如「摸鱼」"
                className="w-full rounded-full border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-primary-400"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSort(s.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                    sort === s.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-primary-100'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-wrap gap-2">
          <Link
            to="/comics"
            className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
              cat === 'all' ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-accent-100'
            }`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/comics?cat=${c.id}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                cat === c.id ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-accent-100'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共 <span className="font-medium text-primary-600">{filtered.length}</span> 篇短篇{keyword && ` · 关键词「${keyword}」`}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {comic.isHot && (
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                    <i className="ri-fire-fill"></i>爆笑
                  </span>
                )}
                {comic.isNew && (
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                    新更
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
                  <span className="text-primary-600">{comic.categoryName}</span>
                  <span>·</span>
                  <span>{comic.episode}</span>
                  <span>·</span>
                  <span>{comic.updatedAt}</span>
                </div>
                <h2 className="mt-2 font-medium text-foreground-950">{comic.title}</h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{comic.summary}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{t}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-600">
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i>{comic.views}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line"></i>{comic.likes}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i>{comic.readingTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-20 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-600">没有找到相关漫画，换个关键词试试吧</p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}